import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

interface SmartLinkProps {
    href: string
    children: ReactNode
    className?: string
    pendingClassName?: string
    tone?: 'light' | 'dark'
    onNavigate?: () => void
}

interface SoonBadgeProps {
    tone?: 'light' | 'dark'
    className?: string
}

export const SoonBadge: FC<SoonBadgeProps> = ({ tone = 'light', className }): ReactNode => {
    return (
        <span
            className={cn(
                'inline-flex items-center rounded-full border px-1.5 py-px text-[9.5px] font-semibold uppercase tracking-[0.14em]',
                tone === 'light' ? 'border-ink/15 text-ink-soft' : 'border-ivory/20 text-ivory/55',
                className
            )}
        >
            Soon
        </span>
    )
}

/*
 * Internal paths use client navigation, absolute URLs open in a new tab, and an
 * empty href renders a visible "soon" placeholder instead of an invented link.
 */
const SmartLink: FC<SmartLinkProps> = ({ href, children, className, pendingClassName, tone = 'light', onNavigate }): ReactNode => {
    if (!href) {
        return (
            <span
                role='link'
                aria-disabled='true'
                className={cn('inline-flex cursor-default items-center gap-2', pendingClassName ?? className)}
            >
                {children}
                <SoonBadge tone={tone} />
                <span className='sr-only'>
                    (link not available yet)
                </span>
            </span>
        )
    }

    if (href.startsWith('/') || href.startsWith('#')) {
        return (
            <Link href={href} className={className} onClick={onNavigate}>
                {children}
            </Link>
        )
    }

    return (
        <a href={href} className={className} target='_blank' rel='noopener noreferrer' onClick={onNavigate}>
            {children}
        </a>
    )
}

export default SmartLink
